"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Selection, SortDescriptor } from "react-aria-components";
import { useAppSelector } from "@hs/shared/stores/hooks";
import type { SortingState, PaginationState } from "@hs/ui";
import {
  AlertDialog,
  Avatar,
  Button,
  cn,
  Dropdown,
  Label,
  ListBox,
  Pagination,
  SearchField,
  Separator,
  toast,
} from "@heroui/react";
import { DataGrid, InlineSelect } from "@heroui-pro/react";
import type { DataGridColumn } from "@heroui-pro/react";
import { IconPlus as Plus, IconRefresh as RefreshCw, IconDotsVertical as MoreVertical, IconTrash as Trash2 } from "@tabler/icons-react";
import { trpc } from "@/lib/trpc";
import type { ContactsOrgNode } from "../types";
import { SPACE_ROOT_ID } from "../types";
import { PeopleEditorDialog, PeopleEditorMode } from "./PeopleEditorDialog";
import { InviteRegisterDialog } from "./InviteRegisterDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const ROW_ACTIONS_ID = "__actions__";
const DEFAULT_PAGE_SIZE = 20;

function sortingToDescriptor(sorting: SortingState): SortDescriptor | undefined {
  const s = sorting[0];
  if (!s) return undefined;
  return { column: s.id, direction: s.desc ? "descending" : "ascending" };
}

function descriptorToSorting(descriptor: SortDescriptor | undefined): SortingState {
  if (!descriptor?.column) return [];
  return [{ id: String(descriptor.column), desc: descriptor.direction === "descending" }];
}

function buildPaginationPages(totalPages: number, safePage: number): (number | "ellipsis")[] {
  const pages: (number | "ellipsis")[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push("ellipsis");
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages);
  }
  return pages;
}

interface ContactsListProps {
  selectedOrg?: ContactsOrgNode | undefined;
}

export function ContactsList({ selectedOrg }: ContactsListProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<PeopleEditorMode>("create");
  const [editingPeople, setEditingPeople] = useState<Record<string, unknown> | null>(null);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [peopleToRemove, setPeopleToRemove] = useState<Record<string, unknown> | null>(null);
  const [inviteRegisterPerson, setInviteRegisterPerson] = useState<Record<string, unknown> | null>(null);
  const [isInviteRegisterOpen, setIsInviteRegisterOpen] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState<{
    userId?: string;
    peopleId?: string;
    displayName: string;
    fullName?: string | null;
    jobNumber?: string | null;
    username?: string | null;
  } | null>(null);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [unlinkPerson, setUnlinkPerson] = useState<Record<string, unknown> | null>(null);
  const [isUnlinkDialogOpen, setIsUnlinkDialogOpen] = useState(false);
  const [isBatchDeleteDialogOpen, setIsBatchDeleteDialogOpen] = useState(false);

  const user = useAppSelector((state) => state.auth.user);
  const spaceId = user?.spaces?.[0]?.id;

  const selectedOrgId = selectedOrg?.id;
  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setSelectedKeys(new Set());
  }, [selectedOrgId]);

  const sortBy = useMemo(() => (sorting.length ? sorting[0].id : undefined), [sorting]);
  const sortOrder = useMemo(() => (sorting.length ? (sorting[0].desc ? "desc" : "asc") : undefined), [sorting]);
  const isSpaceRoot = selectedOrg?.id === SPACE_ROOT_ID;

  const listSpacePeopleQuery = trpc.contacts.listSpacePeople.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      keyword: searchKeyword.trim() || undefined,
      sortBy: sortBy as "fullName" | "jobNumber" | "mobile" | "email" | undefined,
      sortOrder: sortOrder as "asc" | "desc" | undefined,
    },
    { enabled: isSpaceRoot }
  );

  const getPeopleByOrgIdQuery = trpc.contacts.getPeopleByOrgId.useQuery(
    {
      id: (selectedOrg?.id as string) || "",
      queryInput: {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        keyword: searchKeyword.trim() || undefined,
        sortBy: sortBy as "fullName" | "jobNumber" | "mobile" | "email" | undefined,
        sortOrder: sortOrder as "asc" | "desc" | undefined,
      },
    },
    { enabled: !!selectedOrg?.id && !isSpaceRoot }
  );

  const { data, isLoading, refetch } = isSpaceRoot ? listSpacePeopleQuery : getPeopleByOrgIdQuery;

  type PeopleListData = NonNullable<typeof data>;
  type People = PeopleListData["items"][number];

  const people = (data?.items ?? []) as People[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / (pagination.pageSize || DEFAULT_PAGE_SIZE))),
    [total, pagination.pageSize]
  );

  const handleEdit = useCallback((row: People) => {
    setDialogMode("edit");
    setEditingPeople(row as unknown as Record<string, unknown>);
    setIsDialogOpen(true);
  }, []);

  const handleRemove = useCallback((row: People) => {
    setPeopleToRemove(row as unknown as Record<string, unknown>);
    setIsRemoveDialogOpen(true);
  }, []);

  const renderActions = useCallback(
    (person: People) => {
      const items: { id: string; label: string; variant?: "danger"; onPress: () => void }[] = [];
      if (person.user != null) {
        const displayName: string = person.fullName || "该用户";
        items.push({
          id: "reset",
          label: "重置密码",
          onPress: () => {
            setResetPasswordTarget({
              peopleId: person.id ?? undefined,
              displayName,
              fullName: person.fullName ?? undefined,
              jobNumber: person.jobNumber ?? undefined,
            });
            setIsResetPasswordOpen(true);
          },
        });
        items.push({
          id: "unlink",
          label: "解除员工关连",
          onPress: () => {
            setUnlinkPerson(person as unknown as Record<string, unknown>);
            setIsUnlinkDialogOpen(true);
          },
        });
      }
      items.push({ id: "edit", label: "编辑", onPress: () => handleEdit(person) });
      items.push({
        id: "delete",
        label: "删除",
        variant: "danger",
        onPress: () => handleRemove(person),
      });

      return (
        <div className="flex justify-center">
          <Dropdown>
            <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
              <MoreVertical className="size-4" stroke={1.5} />
            </Button>
            <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
              <Dropdown.Menu>
                {items.map((it) => (
                  <Dropdown.Item
                    key={it.id}
                    id={it.id}
                    textValue={it.label}
                    variant={it.variant}
                    onAction={it.onPress}
                  >
                    <Label>{it.label}</Label>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        </div>
      );
    },
    [handleEdit, handleRemove]
  );

  const dataColumns = useMemo<DataGridColumn<People>[]>(
    () => [
      {
        id: "fullName",
        accessorKey: "fullName",
        header: "姓名",
        minWidth: 200,
        allowsSorting: true,
        allowsResizing: true,
        isRowHeader: true,
        cell: (row) => {
          const fullName = row.fullName || "";
          const avatarUrl = row.avatarUrl;
          const initial =
            fullName.split("").find((char) => /[\u4e00-\u9fa5]/.test(char)) ||
            fullName.charAt(0).toUpperCase() ||
            "";
          return (
            <div className="flex items-center gap-3">
              <Avatar className="size-8 shrink-0" variant="soft">
                {avatarUrl ? <Avatar.Image src={avatarUrl} alt={fullName} /> : null}
                <Avatar.Fallback>{initial}</Avatar.Fallback>
              </Avatar>
              <span className="text-sm font-medium">{fullName}</span>
            </div>
          );
        },
      },
      {
        id: "jobNumber",
        accessorKey: "jobNumber",
        header: "工号",
        minWidth: 100,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">{row.jobNumber || "—"}</span>
        ),
      },
      {
        id: "mobile",
        accessorKey: "mobile",
        header: "手机号",
        minWidth: 120,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => <span className="text-sm">{row.mobile || "—"}</span>,
      },
      {
        id: "email",
        accessorKey: "email",
        header: "邮箱",
        minWidth: 160,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">{row.email || "—"}</span>
        ),
      },
      {
        id: "registered",
        header: "是否注册",
        minWidth: 120,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => {
          if (row.user != null) {
            return <span className="text-muted-foreground text-sm">已注册</span>;
          }
          return (
            <Button
              size="sm"
              variant="secondary"
              onPress={() => {
                setInviteRegisterPerson(row as unknown as Record<string, unknown>);
                setIsInviteRegisterOpen(true);
              }}
            >
              邀请注册
            </Button>
          );
        },
      },
    ],
    []
  );

  const actionsColumn = useMemo<DataGridColumn<People>>(
    () => ({
      id: ROW_ACTIONS_ID,
      header: "",
      align: "end",
      width:70,
      minWidth: 70,
      maxWidth: 70,
      allowsSorting: false,
      allowsResizing: false,
      pinned: "end",
      cell: (item) => (
        <div className="flex min-w-0 justify-end gap-1 overflow-hidden">{renderActions(item)}</div>
      ),
    }),
    [renderActions]
  );

  const columns = useMemo(() => [...dataColumns, actionsColumn], [dataColumns, actionsColumn]);

  const sortDescriptor = useMemo(() => sortingToDescriptor(sorting), [sorting]);
  const sortableColumnIds = useMemo(() => new Set(["fullName", "jobNumber", "mobile", "email"]), []);

  const columnsWithRowHeader = useMemo(() => {
    const first = columns.find((c) => c.id !== ROW_ACTIONS_ID);
    const fh = first?.id;
    return columns.map((c) => ({
      ...c,
      isRowHeader: c.id === fh,
      allowsSorting: sortableColumnIds.has(String(c.id)) ? true : (c.allowsSorting ?? false),
    }));
  }, [columns, sortableColumnIds]);

  const handleSortChange = useCallback((d: SortDescriptor) => {
    setSorting(descriptorToSorting(d));
  }, []);

  const handleRefresh = useCallback(() => void refetch(), [refetch]);

  const deletePeopleMutation = trpc.contacts.deletePerson.useMutation({
    onSuccess: () => {
      toast.success("人员删除成功");
      setIsRemoveDialogOpen(false);
      setPeopleToRemove(null);
      void refetch();
    },
  });

  const deleteManyPeopleMutation = trpc.contacts.deleteManyPeople.useMutation({
    onSuccess: (d) => {
      toast.success(`批量删除成功，共删除 ${d.count} 人`);
      setIsBatchDeleteDialogOpen(false);
      setSelectedKeys(new Set());
      void refetch();
    },
  });

  const handleConfirmRemove = useCallback(() => {
    const id = peopleToRemove?.id as string | undefined;
    if (id) deletePeopleMutation.mutate({ id });
  }, [peopleToRemove, deletePeopleMutation]);

  const selectedCount =
    selectedKeys === "all" ? people.length : selectedKeys instanceof Set ? selectedKeys.size : 0;

  const handleBatchDeleteClick = useCallback(() => {
    if (selectedCount === 0) {
      toast.warning("请先选择要删除的人员");
      return;
    }
    setIsBatchDeleteDialogOpen(true);
  }, [selectedCount]);

  const handleConfirmBatchDelete = useCallback(() => {
    let ids: string[] = [];
    if (selectedKeys === "all") {
      ids = people.map((p) => p.id).filter(Boolean) as string[];
    } else if (selectedKeys instanceof Set) {
      ids = [...selectedKeys].map(String);
    }
    if (ids.length === 0) return;
    deleteManyPeopleMutation.mutate({ ids });
  }, [selectedKeys, people, deleteManyPeopleMutation]);

  const unlinkUserMutation = trpc.contacts.updatePerson.useMutation({
    onSuccess: () => {
      toast.success("已解除员工关连");
      setIsUnlinkDialogOpen(false);
      setUnlinkPerson(null);
      void refetch();
    },
  });

  const handleConfirmUnlink = useCallback(() => {
    const id = unlinkPerson?.id as string | undefined;
    if (id) unlinkUserMutation.mutate({ id, data: { userId: null } });
  }, [unlinkPerson, unlinkUserMutation]);

  const safePage = Math.min(pagination.pageIndex + 1, pageCount);
  const paginationPages = useMemo(() => buildPaginationPages(pageCount, safePage), [pageCount, safePage]);
  const pageSizes = [10, 20, 50, 100];

  const getRowId = useCallback((row: People) => String(row.id ?? ""), []);

  if (!selectedOrg) {
    return (
      <div className="flex h-full flex-1 items-center justify-center">
        <p className="text-muted-foreground text-lg">请从左侧选择组织查看联系人</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-14 shrink-0 items-center border-b border-[var(--border)] px-4">
        <h1 className="text-2xl font-bold">{selectedOrg.name}</h1>
      </div>
      <div className={cn("bg-background flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4")}>
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={cn(
              "grid min-h-0 min-w-0 w-full flex-1 gap-y-3",
              "grid-rows-[auto_minmax(0,1fr)_auto]"
            )}
          >
            <div className="relative z-10 flex min-w-0 shrink-0 flex-col gap-3">
              <div className="flex w-full min-w-0 shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <SearchField
                    aria-label="搜索人员"
                    value={searchKeyword}
                    onChange={(v) => {
                      setSearchKeyword(v);
                      setPagination((p) => ({ ...p, pageIndex: 0 }));
                    }}
                  >
                    <SearchField.Group>
                      <SearchField.SearchIcon />
                      <SearchField.Input
                        className="w-[min(100%,220px)] min-w-[160px]"
                        placeholder="搜索姓名、工号"
                      />
                      <SearchField.ClearButton />
                    </SearchField.Group>
                  </SearchField>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  <Button variant="secondary" onPress={handleRefresh}>
                    <RefreshCw className="size-4" stroke={1.5} />
                    刷新
                  </Button>
                  <Button
                    variant="danger"
                    isDisabled={selectedCount === 0}
                    onPress={handleBatchDeleteClick}
                  >
                    <Trash2 className="size-4" stroke={1.5} />
                    删除
                  </Button>
                  <Button onPress={() => {
                    setDialogMode("create");
                    setEditingPeople(null);
                    setIsDialogOpen(true);
                  }}
                  >
                    <Plus className="size-4" stroke={1.5} />
                    添加人员
                  </Button>
                </div>
              </div>
            </div>

            <div className="relative min-h-0 min-w-0 overflow-hidden">
              <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
                {isLoading ? (
                  <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center text-sm">
                    加载中…
                  </div>
                ) : null}
                <DataGrid<People>
                  allowsColumnResize
                  aria-label="联系人列表"
                  className="flex h-full min-h-0 min-w-0 flex-col !overflow-visible"
                  columns={columnsWithRowHeader}
                  contentClassName="min-w-0 !overflow-visible"
                  data={people}
                  defaultSelectedKeys={undefined}
                  getRowId={getRowId}
                  renderEmptyState={() => (
                    <div className="text-muted-foreground py-8 text-center text-sm">
                      {isSpaceRoot ? "该空间暂无人员" : "该组织下暂无联系人"}
                    </div>
                  )}
                  scrollContainerClassName={cn(
                    "h-full max-h-full min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain",
                    "[&_[data-slot=table-resizable-container]]:overflow-visible",
                    "[&_[data-slot=table-content]]:!overflow-visible",
                    "[&_thead_th]:bg-[var(--surface-secondary)] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20",
                    "[&_th]:min-w-0 [&_td]:min-w-0",
                    "[&_[role=columnheader]]:min-w-0 [&_[role=gridcell]]:min-w-0"
                  )}
                  selectedKeys={selectedKeys}
                  selectionMode="multiple"
                  showSelectionCheckboxes
                  sortDescriptor={sortDescriptor}
                  variant="primary"
                  onSelectionChange={setSelectedKeys}
                  onSortChange={handleSortChange}
                />
              </div>
            </div>

            <div className="text-muted-foreground flex min-w-0 shrink-0 flex-nowrap items-center justify-between gap-3 overflow-x-auto text-sm">
              <div className="flex w-fit shrink-0 items-center">
                <Pagination className="w-fit max-w-none" size="sm">
                  <Pagination.Content className="flex w-fit min-w-0">
                    <Pagination.Item>
                      <Pagination.Previous
                        isDisabled={safePage <= 1}
                        onPress={() =>
                          setPagination((p) => ({
                            ...p,
                            pageIndex: Math.max(0, p.pageIndex - 1),
                          }))
                        }
                      >
                        <Pagination.PreviousIcon />
                      </Pagination.Previous>
                    </Pagination.Item>
                    {paginationPages.map((p, i) =>
                      p === "ellipsis" ? (
                        <Pagination.Item key={`e-${i}`}>
                          <Pagination.Ellipsis />
                        </Pagination.Item>
                      ) : (
                        <Pagination.Item key={p}>
                          <Pagination.Link
                            isActive={p === safePage}
                            onPress={() =>
                              setPagination((prev) => ({
                                ...prev,
                                pageIndex: (p as number) - 1,
                              }))
                            }
                          >
                            {p}
                          </Pagination.Link>
                        </Pagination.Item>
                      )
                    )}
                    <Pagination.Item>
                      <Pagination.Next
                        isDisabled={safePage >= pageCount}
                        onPress={() =>
                          setPagination((p) => ({
                            ...p,
                            pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
                          }))
                        }
                      >
                        <Pagination.NextIcon />
                      </Pagination.Next>
                    </Pagination.Item>
                  </Pagination.Content>
                </Pagination>
              </div>

              <div className="flex h-4 w-fit shrink-0 flex-nowrap items-center gap-3 whitespace-nowrap">
                <InlineSelect
                  aria-label="每页条数"
                  value={String(pagination.pageSize)}
                  onChange={(v) => {
                    if (v) {
                      setPagination((p) => ({
                        ...p,
                        pageSize: Number(v),
                        pageIndex: 0,
                      }));
                    }
                  }}
                >
                  <InlineSelect.Trigger>
                    <span className="text-muted">每页</span>
                    <InlineSelect.Value />
                    <InlineSelect.Indicator />
                  </InlineSelect.Trigger>
                  <InlineSelect.Popover className="w-[88px]">
                    <ListBox>
                      {pageSizes.map((n) => (
                        <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                          {n}
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </InlineSelect.Popover>
                </InlineSelect>
                <Separator className="!h-4 shrink-0" orientation="vertical" />
                <span className="shrink-0 text-muted">
                  第 {total === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1}–
                  {Math.min(safePage * pagination.pageSize, total)} 条，共 {total} 条
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PeopleEditorDialog
        open={isDialogOpen}
        mode={dialogMode}
        peopleData={editingPeople ?? undefined}
        defaultOrgIds={
          selectedOrg?.id && selectedOrg.id !== SPACE_ROOT_ID ? [selectedOrg.id] : undefined
        }
        onOpenChange={setIsDialogOpen}
        onSuccess={() => void refetch()}
      />

      <InviteRegisterDialog
        open={isInviteRegisterOpen}
        onOpenChange={(open) => {
          setIsInviteRegisterOpen(open);
          if (!open) setInviteRegisterPerson(null);
        }}
        person={
          inviteRegisterPerson as {
            id: string;
            fullName?: string | null;
            email?: string | null;
          } | null
        }
        spaceId={spaceId}
        onSuccess={refetch}
      />

      <ResetPasswordDialog
        open={isResetPasswordOpen}
        onOpenChange={(open) => {
          setIsResetPasswordOpen(open);
          if (!open) setResetPasswordTarget(null);
        }}
        target={resetPasswordTarget}
        onSuccess={refetch}
      />

      <AlertDialog
        isOpen={isBatchDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsBatchDeleteDialogOpen(open);
          if (!open) setSelectedKeys(new Set());
        }}
      >
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认批量删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除已选中的 {selectedCount} 名人员吗？删除后不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setIsBatchDeleteDialogOpen(false);
                    setSelectedKeys(new Set());
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteManyPeopleMutation.isPending}
                  onPress={handleConfirmBatchDelete}
                >
                  {deleteManyPeopleMutation.isPending ? "删除中…" : "确认删除"}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <AlertDialog isOpen={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除人员「{String(peopleToRemove?.fullName)}」吗？删除后不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setIsRemoveDialogOpen(false);
                    setPeopleToRemove(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deletePeopleMutation.isPending}
                  onPress={handleConfirmRemove}
                >
                  {deletePeopleMutation.isPending ? "删除中…" : "确认删除"}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <AlertDialog isOpen={isUnlinkDialogOpen} onOpenChange={setIsUnlinkDialogOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>解除员工关连</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                {unlinkPerson ? (
                  <>确定要解除 {String(unlinkPerson.fullName ?? "该人员")} 与系统用户的关连吗？</>
                ) : (
                  ""
                )}
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setIsUnlinkDialogOpen(false);
                    setUnlinkPerson(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  isDisabled={unlinkUserMutation.isPending}
                  variant="primary"
                  onPress={handleConfirmUnlink}
                >
                  {unlinkUserMutation.isPending ? "处理中…" : "确定"}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </>
  );
}
